import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Circle, LogIn, Monitor, ShieldAlert, Timer, UserX } from 'lucide-react';
import { clsx } from 'clsx';

import { api } from '../lib/api.ts';
import { dataHora, desde, duracao, numero } from '../lib/formato.ts';
import { Aviso, Carregando, Cartao, Etiqueta, Selecao, Tabela } from '../componentes/ui.tsx';

interface Sessao {
  id: string;
  usuarioId: string;
  nome: string;
  email: string;
  perfil: string;
  criadaEm: string;
  ultimaAtividade: string;
  encerradaEm: string | null;
  motivo: string | null;
  online: boolean;
  duracaoSegundos: number;
  ip: string | null;
  navegador: string | null;
}

interface RespostaSessoes {
  minutosParaFicarOffline: number;
  periodoEmDias: number;
  online: Sessao[];
  sessoes: Sessao[];
  nuncaEntraram: { id: string; nome: string; perfil: string }[];
  maxTentativas: number;
  minutosDeBloqueio: number;
  bloqueados: { email: string; falhas: number; bloqueadoAte: string }[];
  tentativasRecusadas: { email: string; motivo: string | null; ip: string | null; quando: string }[];
}

const MOTIVO_DA_RECUSA: Record<string, string> = {
  senha_errada: 'senha errada',
  email_desconhecido: 'e-mail não cadastrado',
  bloqueado: 'já estava bloqueado',
  desativado: 'conta desativada',
};

interface PorUsuario {
  id: string;
  nome: string;
  email: string;
  perfil: string;
  sessoes: number;
  totalSegundos: number;
  ultimaAtividade: string | null;
  online: boolean;
}

const PERIODOS = ['7', '30', '90', '365'];

const NOME_DO_PERIODO: Record<string, string> = {
  '7': 'Últimos 7 dias',
  '30': 'Últimos 30 dias',
  '90': 'Últimos 90 dias',
  '365': 'Último ano',
};

/** O navegador em uma palavra. O user agent inteiro não cabe e ninguém lê. */
function navegadorCurto(ua: string | null): string {
  if (ua === null || ua === '') return '—';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('Chrome/') && !ua.includes('Chromium')) return 'Chrome';
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Safari/')) return 'Safari';
  return 'Outro';
}

function ComoTerminou({ sessao }: { sessao: Sessao }) {
  if (sessao.online) return <Etiqueta tom="bom">em uso</Etiqueta>;
  if (sessao.motivo === 'saiu') return <Etiqueta tom="neutro">saiu</Etiqueta>;
  if (sessao.motivo === 'expirou') return <Etiqueta tom="medio">prazo venceu</Etiqueta>;
  // Sem motivo e sem estar online: parou de usar, mas o prazo ainda não venceu.
  return <Etiqueta tom="neutro">parou de usar</Etiqueta>;
}

/**
 * Sessões e acessos.
 *
 * Cada login abre uma sessão; esta tela lê essas sessões. Online não é um
 * campo gravado, e sim quem teve atividade nos últimos minutos — quem fecha o
 * navegador não avisa ninguém, então um booleano no banco ficaria preso em
 * "sim" para sempre.
 */
export function Sessoes() {
  const [dias, setDias] = useState('30');

  const consulta = useQuery({
    queryKey: ['sessoes', dias],
    queryFn: () => api<RespostaSessoes>(`/sessoes?dias=${dias}`),
    // Quem está online muda sozinho: sem recarregar, a tela envelheceria em
    // silêncio e mostraria gente online que já saiu.
    refetchInterval: 30_000,
  });

  const resumo = useQuery({
    queryKey: ['sessoes-por-usuario', dias],
    queryFn: () => api<{ usuarios: PorUsuario[] }>(`/sessoes/por-usuario?dias=${dias}`),
    refetchInterval: 60_000,
  });

  if (consulta.isLoading) return <Carregando texto="Lendo as sessões…" />;
  if (consulta.isError) {
    return (
      <Aviso tom="erro" titulo="Não foi possível carregar as sessões">
        <p>{(consulta.error as Error).message}</p>
      </Aviso>
    );
  }

  const dados = consulta.data;
  if (dados === undefined) return null;

  const usuarios = resumo.data?.usuarios ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
            <Monitor className="size-6 text-slate-400" />
            Sessões e acessos
          </h1>
          <p className="mt-1 max-w-prose text-sm text-slate-600">
            Cada login abre uma sessão. Fica como <strong>online</strong> quem teve atividade nos
            últimos {dados.minutosParaFicarOffline} minutos — quem fecha o navegador não avisa
            ninguém, então a atividade é o único sinal que existe.
          </p>
        </div>
        <div className="w-48">
          <Selecao
            rotulo="Período"
            valor={dias}
            opcoes={PERIODOS}
            permiteVazio={false}
            rotuloDaOpcao={(v) => NOME_DO_PERIODO[v] ?? `${v} dias`}
            onChange={setDias}
          />
        </div>
      </div>

      {/* --------------------------------------------------------- online */}
      <Cartao
        titulo={
          dados.online.length === 0
            ? 'Ninguém no sistema agora'
            : `${dados.online.length} ${dados.online.length === 1 ? 'pessoa' : 'pessoas'} no sistema agora`
        }
        descricao="Contado por pessoa: quem está com o sistema aberto no computador e no celular aparece uma vez só."
      >
        {dados.online.length === 0 ? (
          <p className="text-sm text-slate-500">
            A última atividade registrada foi{' '}
            {dados.sessoes[0] === undefined ? 'nenhuma' : desde(dados.sessoes[0].ultimaAtividade)}.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {dados.online.map((s) => (
              <div key={s.id} className="rounded border border-emerald-200 bg-emerald-50/50 p-3">
                <p className="flex items-center gap-2 font-medium text-slate-900">
                  <Circle className="size-2.5 fill-emerald-500 text-emerald-500" />
                  {s.nome}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">{s.perfil}</p>
                <div className="mt-2 space-y-0.5 text-xs text-slate-600">
                  <p className="flex items-center gap-1.5">
                    <LogIn className="size-3 text-slate-400" />
                    entrou {dataHora(s.criadaEm)}
                  </p>
                  <p className="flex items-center gap-1.5">
                    <Timer className="size-3 text-slate-400" />
                    {duracao(s.duracaoSegundos)} de sessão · ativo {desde(s.ultimaAtividade)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Cartao>

      {/* --------------------------------------------------- por usuário */}
      <Cartao
        titulo="Uso por pessoa"
        descricao={`Somando as sessões dos ${NOME_DO_PERIODO[dias]?.toLowerCase() ?? `${dias} dias`}.`}
      >
        <Tabela
          cabecalho={
            <tr>
              <th className="py-2 pr-3">Pessoa</th>
              <th className="py-2 pr-3">Perfil</th>
              <th className="py-2 pr-3 text-right">Entradas</th>
              <th className="py-2 pr-3 text-right">Tempo somado</th>
              <th className="py-2 pr-3">Última atividade</th>
            </tr>
          }
        >
          {usuarios.map((u) => (
            <tr key={u.id} className={clsx('hover:bg-slate-50', u.sessoes === 0 && 'text-slate-400')}>
              <td className="py-2 pr-3">
                <span className="flex items-center gap-2">
                  {u.online && <Circle className="size-2 fill-emerald-500 text-emerald-500" />}
                  <span className={clsx(!u.online && 'pl-4')}>{u.nome}</span>
                </span>
                <span className="block pl-4 text-xs text-slate-500">{u.email}</span>
              </td>
              <td className="py-2 pr-3 text-slate-600">{u.perfil}</td>
              <td className="num py-2 pr-3">{u.sessoes === 0 ? '—' : numero(u.sessoes)}</td>
              <td className="num py-2 pr-3">
                {u.sessoes === 0 ? '—' : duracao(u.totalSegundos)}
              </td>
              <td className="py-2 pr-3 text-slate-600">
                {u.ultimaAtividade === null ? 'nunca entrou' : desde(u.ultimaAtividade)}
              </td>
            </tr>
          ))}
        </Tabela>
      </Cartao>

      {/* ---------------------------------------------------- histórico */}
      <Cartao
        titulo={`${numero(dados.sessoes.length)} ${dados.sessoes.length === 1 ? 'entrada' : 'entradas'} no período`}
        descricao="O tempo conta do login até a última atividade, não até agora: uma aba esquecida aberta não vira dez horas de uso."
      >
        {dados.sessoes.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhuma entrada registrada neste período.</p>
        ) : (
          <Tabela
            cabecalho={
              <tr>
                <th className="py-2 pr-3">Pessoa</th>
                <th className="py-2 pr-3">Entrou</th>
                <th className="py-2 pr-3">Última atividade</th>
                <th className="py-2 pr-3 text-right">Duração</th>
                <th className="py-2 pr-3">Como terminou</th>
                <th className="py-2 pr-3">Origem</th>
              </tr>
            }
          >
            {dados.sessoes.map((s) => (
              <tr key={s.id} className="hover:bg-slate-50">
                <td className="py-2 pr-3 whitespace-nowrap">{s.nome}</td>
                <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
                  {dataHora(s.criadaEm)}
                </td>
                <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
                  {dataHora(s.ultimaAtividade)}
                </td>
                <td className="num py-2 pr-3">{duracao(s.duracaoSegundos)}</td>
                <td className="py-2 pr-3">
                  <ComoTerminou sessao={s} />
                </td>
                <td className="py-2 pr-3 text-xs text-slate-500" title={s.navegador ?? undefined}>
                  {navegadorCurto(s.navegador)}
                  {s.ip !== null && ` · ${s.ip}`}
                </td>
              </tr>
            ))}
          </Tabela>
        )}
      </Cartao>

      {/* ------------------------------------------------ acessos recusados */}
      {dados.bloqueados.length > 0 && (
        <Aviso
          tom="erro"
          titulo={`${dados.bloqueados.length} ${dados.bloqueados.length === 1 ? 'acesso bloqueado agora' : 'acessos bloqueados agora'}`}
        >
          <p className="mb-2">
            Bloqueio automático depois de {dados.maxTentativas} tentativas erradas seguidas. Sai
            sozinho quando o tempo passa — ninguém precisa destravar.
          </p>
          <ul className="space-y-0.5">
            {dados.bloqueados.map((b) => (
              <li key={b.email} className="flex items-center gap-1.5 text-sm">
                <ShieldAlert className="size-3.5 shrink-0 text-red-600" />
                <span className="font-medium">{b.email}</span>
                <span className="text-slate-600">
                  · {b.falhas} erros · libera {dataHora(b.bloqueadoAte)}
                </span>
              </li>
            ))}
          </ul>
        </Aviso>
      )}

      <Cartao
        titulo={`${numero(dados.tentativasRecusadas.length)} ${dados.tentativasRecusadas.length === 1 ? 'tentativa recusada' : 'tentativas recusadas'} nos últimos 7 dias`}
        descricao={`Depois de ${dados.maxTentativas} erros seguidos, o acesso trava por ${dados.minutosDeBloqueio} minutos. Um punhado de linhas é gente esquecendo a senha; dezenas de madrugada não é.`}
      >
        {dados.tentativasRecusadas.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhuma tentativa recusada no período.</p>
        ) : (
          <Tabela
            cabecalho={
              <tr>
                <th className="py-2 pr-3">Quando</th>
                <th className="py-2 pr-3">E-mail digitado</th>
                <th className="py-2 pr-3">Motivo</th>
                <th className="py-2 pr-3">Origem</th>
              </tr>
            }
          >
            {dados.tentativasRecusadas.map((t, i) => (
              <tr key={`${t.quando}-${i}`} className="hover:bg-slate-50">
                <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
                  {dataHora(t.quando)}
                </td>
                <td className="py-2 pr-3 whitespace-nowrap">{t.email}</td>
                <td className="py-2 pr-3 text-slate-600">
                  {t.motivo === null ? '—' : (MOTIVO_DA_RECUSA[t.motivo] ?? t.motivo)}
                </td>
                <td className="py-2 pr-3 text-xs text-slate-500">{t.ip ?? '—'}</td>
              </tr>
            ))}
          </Tabela>
        )}
      </Cartao>

      {dados.nuncaEntraram.length > 0 && (
        <Aviso
          titulo={`${dados.nuncaEntraram.length} ${dados.nuncaEntraram.length === 1 ? 'pessoa tem acesso e nunca entrou' : 'pessoas têm acesso e nunca entraram'}`}
        >
          <p className="mb-2">
            Contas ativas que nunca abriram uma sessão. Ou a senha não chegou até a pessoa, ou o
            acesso deixou de ser necessário.
          </p>
          <ul className="space-y-0.5">
            {dados.nuncaEntraram.map((u) => (
              <li key={u.id} className="flex items-center gap-1.5 text-sm">
                <UserX className="size-3.5 shrink-0 text-slate-400" />
                {u.nome} <span className="text-slate-500">· {u.perfil}</span>
              </li>
            ))}
          </ul>
        </Aviso>
      )}

      <p className="text-xs text-slate-500">
        Sessões abertas antes desta tela existir não aparecem: o registro começa no primeiro login
        depois da atualização.
      </p>
    </div>
  );
}
